import LinkOffIcon from '@mui/icons-material/LinkOff';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import { type JSX, useCallback, useMemo } from 'react';

import { LinkByNumberPicker } from '~/components/invoices/detailPage/related/LinkByNumberPicker';
import { RelatedTicketsTable } from '~/components/invoices/detailPage/related/RelatedTicketsTable';
import { useParentTickets } from '~/components/invoices/detailPage/useParentTickets';
import { useInvoiceTicketLinking } from '~/components/invoices/useInvoiceTicketLinking';
import { mapTicketToTicketWithPortfolio } from '~/data/hooks/useTickets';
import type { Invoice } from '~/data/schemas/invoice';
import type { Ticket, TicketWithPortfolio } from '~/data/schemas/ticket';

interface TicketsSectionProps {
  invoice: Invoice;
  /** Tickets already linked to this invoice (`useInvoiceTickets`). */
  tickets: Ticket[] | undefined;
}

/**
 * The tickets linked to this invoice, where they can be linked and unlinked. The wider
 * service history of the elevators it serviced is a view on the Cars Serviced tab — that
 * is about them, not about this invoice.
 */
export function TicketsSection({ invoice, tickets }: TicketsSectionProps) {
  const { ticketOptions, onAddTicket, onRemoveTicket } = useInvoiceTicketLinking(invoice);
  const parentInvoice = invoice.parentInvoice;
  const parentTickets = useParentTickets(invoice, tickets);

  const isFromParent = useCallback(
    (ticket: TicketWithPortfolio) =>
      parentInvoice != null && ticket.invoice?.id === parentInvoice.id,
    [parentInvoice],
  );

  const rowActions = useCallback(
    (ticket: TicketWithPortfolio): JSX.Element[] =>
      isFromParent(ticket)
        ? []
        : [
            <GridActionsCellItem
              key="unlink"
              icon={<LinkOffIcon />}
              label="Unlink from this invoice"
              onClick={() => void onRemoveTicket(ticket)}
              showInMenu
            />,
          ],
    [isFromParent, onRemoveTicket],
  );

  const getRowLabel = useCallback(
    (ticket: TicketWithPortfolio) =>
      isFromParent(ticket) ? `From original invoice #${parentInvoice?.number}` : undefined,
    [isFromParent, parentInvoice?.number],
  );

  const linkOptions = useMemo(
    () =>
      ticketOptions.map(({ ticket, isLinked }) => ({
        id: ticket.id,
        label: `${ticket.number}${isLinked ? ' (Already linked)' : ''}`,
        disabled: isLinked,
      })),
    [ticketOptions],
  );

  const onLink = useCallback(
    async (ticketId: string) => {
      const option = ticketOptions.find(({ ticket }) => ticket.id === ticketId);
      if (option) await onAddTicket(option.ticket);
    },
    [ticketOptions, onAddTicket],
  );

  const rows = useMemo(
    () => [...(tickets ?? []), ...(parentTickets ?? [])].map(mapTicketToTicketWithPortfolio),
    [tickets, parentTickets],
  );

  const picker = (
    <LinkByNumberPicker
      options={linkOptions}
      inputLabel="Ticket Number"
      noOptionsText="No tickets available"
      onLink={onLink}
    />
  );

  return (
    <RelatedTicketsTable
      previewList="invoice_tickets"
      tickets={rows}
      isLoading={tickets === undefined || parentTickets === undefined}
      tableHeaderTitle="Linked tickets"
      rowActions={rowActions}
      getRowLabel={parentInvoice ? getRowLabel : undefined}
      headerContent={picker}
    />
  );
}
